"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { db } from "@/lib/db";
import type { AiProfile } from "@/types/ai";
import { Bot, ChevronDown } from "lucide-react";
import { getUserId } from "@/lib/user/identity";

interface Props {
  currentProfile: AiProfile;
  onSelectAI: (aiId: string) => void;
}

export default function AISelector({ currentProfile, onSelectAI }: Props) {
  const router = useRouter();
  const [allAIs, setAllAIs] = useState<AiProfile[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAIs();
  }, []);

  async function loadAIs() {
    try {
      if (!db || !db.aiProfiles) {
        return;
      }

      const userId = getUserId();
      const ais = await db.aiProfiles
        .where("ownerId")
        .equals(userId)
        .sortBy("updatedAt");
      
      setAllAIs(ais.reverse()); // Newest first
    } catch (error) {
      console.error("Failed to load AIs:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleSelectAI = (ai: AiProfile) => {
    setIsOpen(false);
    onSelectAI(ai.id);
    router.push(`/chat?aiId=${ai.id}`);
  };

  const primaryColor = currentProfile.brand?.primaryColor || "#6366f1";
  const secondaryColor = currentProfile.brand?.secondaryColor || "#8b5cf6";

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:scale-105"
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          color: "white",
          boxShadow: `0 4px 12px ${primaryColor}30`,
        }}
      >
        <Bot className="w-4 h-4" />
        <span className="font-medium text-sm">{currentProfile.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div
            className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-black/10 z-50 max-h-96 overflow-y-auto"
            style={{
              boxShadow: `0 8px 32px ${primaryColor}20`,
            }}
          >
            {loading ? (
              <div className="p-4 text-center text-sm text-gray-500">
                Loading AIs...
              </div>
            ) : allAIs.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No AIs found
              </div>
            ) : (
              <div className="p-2">
                {allAIs.map((ai) => (
                  <button
                    key={ai.id}
                    onClick={() => handleSelectAI(ai)}
                    className={`w-full p-3 rounded-lg text-left transition mb-1 ${
                      ai.id === currentProfile.id
                        ? "bg-gray-100 dark:bg-gray-700"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Bot className={`w-4 h-4 ${
                        ai.id === currentProfile.id
                          ? "text-gray-900 dark:text-gray-100"
                          : "text-gray-600 dark:text-gray-400"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-sm ${
                          ai.id === currentProfile.id
                            ? "text-gray-900 dark:text-gray-100"
                            : "text-gray-700 dark:text-gray-300"
                        }`}>
                          {ai.name}
                        </div>
                        {ai.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                            {ai.description}
                          </div>
                        )}
                      </div>
                      {ai.id === currentProfile.id && (
                        <div
                          className="text-xs px-2 py-0.5 rounded text-white font-medium"
                          style={{
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                          }}
                        >
                          Active
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
